<template>
  <AccordionItem :value="value">
    <AccordionTrigger class="group text-md font-medium">
      {{ label }}
      <template #icon>
        <div class="flex items-center gap-3">
          <router-link :to="`/interview/${slug}?event_type=${eventType}&utm_medium=settings_preview`" @click.stop>
            <Button variant="outline" size="sm">Preview</Button>
          </router-link>
          <ChevronDown
            class="text-muted-foreground size-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180"
          />
        </div>
      </template>
    </AccordionTrigger>
    <AccordionContent>
      <div class="space-y-4 pt-2">
        <div class="space-y-1">
          <Label>{{ label }} Duration</Label>
          <Select :model-value="duration || '30 minutes'" @update:model-value="$emit('update:duration', $event)">
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem v-for="opt in durationOptions" :key="opt" :value="opt">{{ opt }}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div class="space-y-1">
          <Label>{{ label }} Name</Label>
          <Input :model-value="name" @update:model-value="$emit('update:name', $event)" />
        </div>
        <div class="space-y-1">
          <Label>{{ label }} Description</Label>
          <Textarea :rows="3" :model-value="description" @update:model-value="$emit('update:description', $event)" />
        </div>
        <div class="space-y-1">
          <Label>{{ label }} Location</Label>
          <Select :model-value="location || 'phone call'" @update:model-value="$emit('update:location', $event)">
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem v-for="opt in locationOptions" :key="opt" :value="opt">{{ opt }}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Alert v-if="location === 'standard conferencing' && calendarProvider" variant="info" class="mt-2">
          <Video class="h-4 w-4" />
          <AlertDescription> A {{ calendarProvider }} link will be added to the calendar event. </AlertDescription>
        </Alert>
        <AddressInput
          v-if="location == 'physical location'"
          :label="`${label} Address`"
          :model-value="address"
          @update:model-value="$emit('update:address', $event)"
        />
      </div>
    </AccordionContent>
  </AccordionItem>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import AddressInput from '../../shared/AddressInput.vue';
import { ChevronDown, Video } from 'lucide-vue-next';
import { useUserCalendarStore } from '@/stores/userCalendar';

const userCalendarStore = useUserCalendarStore();

const calendarProvider = computed(() => {
  const { isGoogleAuthenticated, isMicrosoftAuthenticated } = userCalendarStore.currentUserCalendar;
  if (isGoogleAuthenticated) return 'Google Meet';
  if (isMicrosoftAuthenticated) return 'Microsoft Teams';
  return null;
});

const durationOptions = ['15 minutes', '30 minutes', '45 minutes', '60 minutes'];
const locationOptions = ['phone call', 'humaniz conferencing', 'standard conferencing', 'physical location'];

defineProps<{
  value: string;
  label: string;
  slug: string;
  eventType: string;
  duration: string;
  name: string;
  description: string;
  location: string;
  address: string;
}>();

defineEmits<{
  'update:duration': [value: string];
  'update:name': [value: string];
  'update:description': [value: string];
  'update:location': [value: string];
  'update:address': [value: string];
}>();
</script>
